import { useMemo, useState } from 'react'
import { Box, Button } from '@mui/material'
import moment from 'moment'
import { useAtomValue, useSetAtom } from 'jotai'
import * as sx from '../../common/stylesInline/report_daily_branch'
import { Item } from '../../common/snippets'
import Datepicker from '../../common/datePicker'
import { ReportHeading } from './common/ReportHeading'
import { GetRequest } from '../../common/api'
import { urls, xlCol, xlColq } from '../../common/constants'
import useToken from '../../helpers/useTokens'
import { companySettingsAtom } from '../../common/jotaiState/commonStateAtoms'
import { alertAtom, globalLoaderAtom } from '../../common/jotaiState/globalStateAtoms'
import { useSearchParams } from 'react-router-dom'
const reportName = 'WhatsApp Message Consumption Report'

const criteriaHeaders = [
  { header: 'Sent From Date', field: 'FromDate', xlCol: { dataType: 'string' } },
  { header: 'Sent To Date', field: 'ToDate', xlCol: { dataType: 'string' } },
]
const WhatsAppConsumptionHeaders = [
  { header: 'SN', exportHead: 'SN', field: 'SN', xlCol: xlCol, weight: 8 },

  {
    header: 'Transaction Date',
    exportHead: 'Transaction Date',
    field: 'Date',
    xlCol: xlCol,
    weight: 18,
  },

  {
    header: 'Entry Type',
    exportHead: 'Entry Type',
    field: 'DRCR_Type',
    xlCol: xlCol,
    weight: 15,
  },

  {
    header: 'Message Count',
    exportHead: 'Message Count',
    field: 'SentCount',
    xlCol: xlColq,
    weight: 18,
  },

  {
    header: 'Session Count',
    exportHead: 'Session Count',
    field: 'SessionCount',
    xlCol: xlColq,
    weight: 18,
  },

  {
    header: 'Opening Balance',
    exportHead: 'Opening Balance',
    field: 'OpeningBalance',
    xlCol: xlColq,
    weight: 20,
  },

  {
    header: 'Txn Amount',
    exportHead: 'Txn Amount',
    field: 'Amount',
    xlCol: xlColq,
    weight: 18,
  },

  {
    header: 'Closing Balance',
    exportHead: 'Closing Balance',
    field: 'ClosingBalance',
    xlCol: xlColq,
    weight: 20,
  },

  {
    header: 'Transaction Remarks',
    exportHead: 'Transaction Remarks',
    field: 'Remarks',
    xlCol: xlCol,
    weight: 25,
  },
]

const openViewReport = (reportPayload) => {
  const rid = Date.now().toString()

  if (!window.generatedReports) {
    window.generatedReports = {}
  }

  window.generatedReports[rid] = reportPayload
  window.open(`/view-report?rid=${rid}`, '_blank')
}

const WhatsAppMessageConsumption = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { loginData } = useToken()
  const { intCompanyID } = loginData || {}
  const companySettings = useAtomValue(companySettingsAtom)
  const { WhatsappTypeID } = companySettings || {}
  const setAlert = useSetAtom(alertAtom)
  const setLoader = useSetAtom(globalLoaderAtom)

  const [fromDate, setFromDate] = useState(moment(new Date()))
  const [toDate, setToDate] = useState(moment(new Date()))

  const criteriaData = useMemo(
    () => ({
      FromDate: moment(fromDate).format('DD-MMM-YYYY'),
      ToDate: moment(toDate).format('DD-MMM-YYYY'),
      dataType: 'entry',
    }),
    [fromDate, toDate]
  )
  const params = {
    view: 'General Settings',
    tab: 521,
  }
  const goToTabs = () => {
    setSearchParams(params)
  }
  const handleGenerateReport = async () => {
    const dateDifference = Math.abs(toDate.diff(fromDate, 'days'))

    if (dateDifference > 31) {
      setAlert({ show: true, msg: 'This report cannot be taken for more than 31 days', nextId: '' })
      return
    }

    setLoader(true)

    try {
      const response = await GetRequest(urls.whatsappTransactionsReport, {
        CompanyID: intCompanyID,
        WhatsappType: WhatsappTypeID || 0,
        FromDate: moment(fromDate).format('YYYY-MM-DD'),
        ToDate: moment(toDate).format('YYYY-MM-DD'),
      })

      if (response?.status !== true) {
        setAlert({ show: true, msg: response?.ErrMessage || response?.Message || 'Request Failed', nextId: '' })
        return
      }
      if (response.Data.Table.length === 0) {
        setAlert({ show: true, msg: 'No Data Found', nextId: '' })
        return
      }

      const tableData = response.Data.Table.map((row, index) => {
        return {
          SN: index + 1,
          ...row,
          dataType: 'entry',
        }
      })
      openViewReport({
        reportName,
        criteriaHeaders,
        criteriaData: [criteriaData],
        tableData,
        mainHeader: WhatsAppConsumptionHeaders,
      })
    } catch (error) {
      setAlert({ show: true, msg: error.message || 'Request Failed', nextId: '' })
    } finally {
      setLoader(false)
    }
  }

  return (
    <Item sx={{ padding: '0' }} className="reports">
      <ReportHeading reportName={reportName.slice(0, -7)} />
      <Box sx={{ ...sx.reportFlex, justifyContent: 'start', paddingTop: '16px !important', gap: '12px' }}>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }} className="input--container bookingDatePicker">
          <Datepicker
            label="Sent From Date"
            name="whatsappMessageConsumptionFromDate"
            id="whatsappMessageConsumptionFromDate"
            value={fromDate}
            endDate={toDate}
            dateChange={setFromDate}
            width="100%"
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }} className="input--container bookingDatePicker">
          <Datepicker
            label="Sent To Date"
            name="whatsappMessageConsumptionToDate"
            id="whatsappMessageConsumptionToDate"
            value={toDate}
            startDate={fromDate}
            dateChange={setToDate}
            width="100%"
          />
        </Item>
        <Item sx={sx.itemsx}>
          <Button
            variant="contained"
            id="whatsappCommunicationLoadButton"
            style={{ ...sx.fullHeight, ...sx.novp }}
            className="btn btn-primary"
            onClick={handleGenerateReport}
          >
            LOAD
          </Button>
        </Item>
        <Item sx={sx.itemsx}>
          <Button style={{ ...sx.fullHeight, ...sx.novp }} className="btn btn-primary" variant="outlined" onClick={() => goToTabs()}>
            Back to Policy
          </Button>
        </Item>
      </Box>
    </Item>
  )
}

export default WhatsAppMessageConsumption
